import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface PerformanceItem {
  id: string;
  orderId: string;
  product: string;
  status: string;
  statusCode: string;
  lockPrice: number;
  actualPrice: number;
  createTime: string;
  completeTime: string | null;
  warehouse: string;
  trackingNo: string;
  weight: number;
  source: 'order' | 'perform';
}

const statusMap: Record<string, { label: string; color: string; bg: string; icon: string; step: number }> = {
  completed: {
    label: '已完成',
    color: 'text-green-600',
    bg: 'bg-green-50 border-green-200',
    icon: 'ri-checkbox-circle-fill',
    step: 3,
  },
  delivering: {
    label: '交割中',
    color: 'text-gold-700',
    bg: 'bg-gold-50 border-gold-200',
    icon: 'ri-truck-fill',
    step: 2,
  },
  processing: {
    label: '履约中',
    color: 'text-gold-700',
    bg: 'bg-gold-50 border-gold-200',
    icon: 'ri-time-fill',
    step: 1,
  },
};

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'processing', label: '进行中' },
  { key: 'completed', label: '已完成' },
];

const stepLabels = ['锁价确认', '物流配送', '履约完成'];

function mapStatus(status: string): string {
  const normalized = status?.trim?.() || '';
  if (normalized === '已完成' || normalized === 'completed') return 'completed';
  if (normalized === '交割中' || normalized === 'delivering') return 'delivering';
  return 'processing';
}

function useCountUp(target: number, duration = 800) {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(0);
  const toRef = useRef(target);

  useEffect(() => {
    fromRef.current = value;
    toRef.current = target;
    startRef.current = null;

    const animate = (timestamp: number) => {
      if (startRef.current === null) startRef.current = timestamp;
      const progress = Math.min((timestamp - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(fromRef.current + (toRef.current - fromRef.current) * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [target, duration]);

  return value;
}

export default function PerformancePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [performanceItems, setPerformanceItems] = useState<PerformanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      if (!userId) {
        setPerformanceItems([]);
        setLoading(false);
        return;
      }

      // 1. 查询已付全款的履约记录
      const { data: performData, error: performErr } = await supabase
        .from('perform_records')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (performErr) throw performErr;

      // 2. 查询未付全款的锁价订单（买料且状态 open）
      const { data: orderData, error: orderErr } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', userId)
        .eq('direction', 'IN')
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      if (orderErr) throw orderErr;

      // 合并两类数据
      const items: PerformanceItem[] = [];

      // 先加入未付全款的订单
      (orderData || []).forEach((row) => {
        items.push({
          id: row.id,
          orderId: row.id,
          product: row.name || '',
          status: '履约中',
          statusCode: 'processing',
          lockPrice: Number(row.total_amount) || 0,
          actualPrice: Number(row.original_margin) || 0,
          createTime: row.create_time || '',
          completeTime: null,
          warehouse: '待支付全款后交割',
          trackingNo: '',
          weight: Number(row.weight) || 0,
          source: 'order',
        });
      });

      // 再加入已完成的履约记录
      (performData || []).forEach((row) => {
        items.push({
          id: row.id,
          orderId: row.order_id || '',
          product: row.product || '',
          status: row.status || '已完成',
          statusCode: mapStatus(row.status),
          lockPrice: Number(row.lock_price) || 0,
          actualPrice: Number(row.actual_price) || 0,
          createTime: row.create_time || '',
          completeTime: row.complete_time || null,
          warehouse: row.warehouse || '',
          trackingNo: row.tracking_no || '',
          weight: 0,
          source: 'perform',
        });
      });

      setPerformanceItems(items);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredItems = activeTab === 'all'
    ? performanceItems
    : performanceItems.filter((item) =>
        activeTab === 'processing'
          ? item.statusCode !== 'completed'
          : item.statusCode === 'completed'
      );

  const itemDetail = performanceItems.find((item) => item.id === selectedItem);

  const totalCount = useCountUp(performanceItems.length);
  const completedCount = useCountUp(performanceItems.filter((i) => i.statusCode === 'completed').length);
  const activeCount = useCountUp(performanceItems.filter((i) => i.statusCode !== 'completed').length);

  const totalAmount = performanceItems.reduce((sum, i) => sum + i.lockPrice, 0);

  return (
    <div className="min-h-screen bg-background-50 pb-28">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-5 py-4">
        <div className="flex items-center justify-center relative">
          <h1 className="text-gold-400 text-lg font-bold">履约管理</h1>
          <span className="absolute right-0 text-gold-400/50 text-xs">
            ¥{(totalAmount / 10000).toFixed(1)}万
          </span>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="bg-gradient-to-br from-brown-900 via-brown-900 to-brown-800 px-4 pt-5 pb-6">
        <div className="grid grid-cols-3 gap-3">
          {/* Total Card */}
          <div className="relative overflow-hidden rounded-xl bg-brown-800/60 border border-gold-500/10 p-3 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent"></div>
            <div className="relative">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gold-500/10 flex items-center justify-center">
                <i className="ri-file-list-3-line text-gold-400 text-sm"></i>
              </div>
              <p className="text-gold-300 text-2xl font-bold leading-none">{totalCount}</p>
              <p className="text-gold-500/50 text-[11px] mt-1.5">总履约单</p>
            </div>
          </div>
          {/* Completed Card */}
          <div className="relative overflow-hidden rounded-xl bg-brown-800/60 border border-green-500/10 p-3 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent"></div>
            <div className="relative">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-green-500/10 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-green-400 text-sm"></i>
              </div>
              <p className="text-green-300 text-2xl font-bold leading-none">{completedCount}</p>
              <p className="text-gold-500/50 text-[11px] mt-1.5">已完成</p>
            </div>
          </div>
          {/* Active Card */}
          <div className="relative overflow-hidden rounded-xl bg-brown-800/60 border border-gold-500/10 p-3 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent"></div>
            <div className="relative">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gold-500/10 flex items-center justify-center">
                <i className="ri-loader-2-line text-gold-400 text-sm animate-spin"></i>
              </div>
              <p className="text-gold-300 text-2xl font-bold leading-none">{activeCount}</p>
              <p className="text-gold-500/50 text-[11px] mt-1.5">进行中</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white px-4 py-3 border-b border-brown-100/80">
        <div className="inline-flex bg-brown-50 rounded-full p-1 gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-5 py-2 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'text-brown-950'
                  : 'text-brown-400 hover:text-brown-600'
              }`}
            >
              {activeTab === tab.key && (
                <span className="absolute inset-0 bg-white rounded-full shadow-sm border border-brown-100"></span>
              )}
              <span className="relative z-10">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Performance List */}
      <div className="px-4 mt-4 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
            <p className="text-brown-400 text-xs">加载中...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-8 text-center border border-brown-100">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brown-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-brown-400 text-xl"></i>
            </div>
            <p className="text-brown-500 text-sm mb-3">履约数据加载失败</p>
            <button
              onClick={fetchData}
              className="px-5 py-2.5 rounded-full bg-gold-500 text-brown-950 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors"
            >
              重新加载
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-brown-100">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brown-50 flex items-center justify-center">
              <i className="ri-inbox-line text-brown-300 text-2xl"></i>
            </div>
            <p className="text-brown-400 text-sm">暂无履约记录</p>
            <p className="text-brown-300 text-xs mt-1">锁价买料后可在履约管理中查看和支付</p>
          </div>
        ) : (
          filteredItems.map((item, index) => {
            const status = statusMap[item.statusCode];
            return (
              <div
                key={item.id}
                onClick={() => setSelectedItem(item.id)}
                className="bg-white rounded-xl border border-brown-100 overflow-hidden cursor-pointer active:scale-[0.99] transition-all duration-200 hover:border-gold-300/50"
                style={{ animationDelay: `${index * 60}ms` }}
              >
                {/* Card Header */}
                <div className="px-4 pt-4 pb-3">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-brown-400 text-[11px] font-mono">{item.id}</span>
                    </div>
                    <div className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${status.bg}`}>
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className={status.icon}></i>
                      </span>
                      <span className={`font-medium ${status.color}`}>{status.label}</span>
                    </div>
                  </div>
                  <h4 className="text-brown-900 text-sm font-bold">{item.product}</h4>
                </div>

                {/* Progress Steps */}
                {item.statusCode !== 'completed' && (
                  <div className="px-4 pb-3">
                    <div className="flex items-center gap-1">
                      {stepLabels.map((label, i) => {
                        const isActive = i < status.step;
                        const isCurrent = i === status.step - 1;
                        return (
                          <div key={label} className="flex items-center flex-1">
                            <div className="flex flex-col items-center flex-1">
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                                  isActive
                                    ? isCurrent
                                      ? 'bg-gold-500 text-brown-950 ring-2 ring-gold-200'
                                      : 'bg-gold-500/20 text-gold-600'
                                    : 'bg-brown-100 text-brown-300'
                                }`}
                              >
                                {isActive && i < status.step - 1 ? (
                                  <i className="ri-check-line"></i>
                                ) : (
                                  i + 1
                                )}
                              </div>
                              <span
                                className={`text-[9px] mt-1 ${
                                  isActive ? 'text-gold-600 font-medium' : 'text-brown-300'
                                }`}
                              >
                                {label}
                              </span>
                            </div>
                            {i < stepLabels.length - 1 && (
                              <div
                                className={`h-0.5 flex-1 mx-1 rounded-full transition-colors ${
                                  i < status.step - 1 ? 'bg-gold-400' : 'bg-brown-100'
                                }`}
                              ></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Completed Badge */}
                {item.statusCode === 'completed' && (
                  <div className="px-4 pb-3">
                    <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 border border-green-100">
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-green-700 text-xs font-medium">履约完成 · 已于 {item.completeTime?.split(' ')[0]} 交割</span>
                    </div>
                  </div>
                )}

                {/* Card Footer Info */}
                <div className="px-4 py-3 bg-brown-50/50 border-t border-brown-100/60">
                  {item.statusCode === 'processing' ? (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                            <i className="ri-money-cny-circle-line text-gold-500 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-brown-400 text-[10px]">已付定金</p>
                            <p className="text-brown-900 text-sm font-bold">¥{item.actualPrice.toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                            <i className="ri-scales-3-line text-red-400 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-brown-400 text-[10px]">待付尾款</p>
                            <p className="text-red-500 text-sm font-bold">
                              ¥{(item.lockPrice - item.actualPrice).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-scales-3-line text-brown-400 text-[10px]"></i>
                          </div>
                          <span className="text-brown-400 text-[10px]">{item.weight}g</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/payment');
                          }}
                          className="px-4 py-2 rounded-lg bg-gold-500 text-brown-950 text-xs font-semibold cursor-pointer whitespace-nowrap active:scale-95 transition-transform"
                        >
                          去支付尾款
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                            <i className="ri-money-cny-circle-line text-gold-500 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-brown-400 text-[10px]">锁价金额</p>
                            <p className="text-brown-900 text-sm font-bold">¥{item.lockPrice.toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                            <i className="ri-building-2-line text-gold-500 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-brown-400 text-[10px]">交割金库</p>
                            <p className="text-brown-900 text-sm font-medium">{item.warehouse}</p>
                          </div>
                        </div>
                      </div>
                      {item.statusCode === 'delivering' && item.trackingNo && (
                        <div className="mt-2.5 pt-2.5 border-t border-brown-100/60 flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-truck-line text-brown-400 text-xs"></i>
                          </div>
                          <span className="text-brown-500 text-xs">物流单号: <span className="font-mono">{item.trackingNo}</span></span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detail Modal */}
      {itemDetail && (
        <div className="fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setSelectedItem(null)}
          ></div>
          <div className="relative bg-white rounded-t-2xl w-full max-h-[85vh] overflow-auto sheet-slide-up pb-24">
            {/* Modal Handle */}
            <div className="sticky top-0 bg-white rounded-t-2xl z-10 px-4 pt-3 pb-0">
              <div className="w-10 h-1 bg-brown-200 rounded-full mx-auto mb-3"></div>
            </div>

            {/* Modal Header */}
            <div className="px-4 pb-4 border-b border-brown-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-brown-900 text-base font-bold">履约详情</h3>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-brown-50 text-brown-400 cursor-pointer hover:bg-brown-100 transition-colors"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-brown-500 text-xs font-mono">{itemDetail.id}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusMap[itemDetail.statusCode].bg} ${statusMap[itemDetail.statusCode].color} font-medium`}>
                  {statusMap[itemDetail.statusCode].label}
                </span>
              </div>
            </div>

            {/* Product Info */}
            <div className="px-4 py-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-400/20 to-gold-600/10 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-gold-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-brown-900 text-sm font-bold">{itemDetail.product}</p>
                  <p className="text-brown-400 text-xs mt-0.5">关联订单 {itemDetail.orderId}</p>
                </div>
              </div>

              {/* Progress for non-completed */}
              {itemDetail.statusCode !== 'completed' && (
                <div className="mb-4 p-3 bg-brown-50 rounded-xl border border-brown-100">
                  <div className="flex items-center gap-1">
                    {stepLabels.map((label, i) => {
                      const isActive = i < statusMap[itemDetail.statusCode].step;
                      const isCurrent = i === statusMap[itemDetail.statusCode].step - 1;
                      return (
                        <div key={label} className="flex items-center flex-1">
                          <div className="flex flex-col items-center flex-1">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${
                                isActive
                                  ? isCurrent
                                    ? 'bg-gold-500 text-brown-950 ring-2 ring-gold-200'
                                    : 'bg-gold-500/20 text-gold-600'
                                  : 'bg-brown-100 text-brown-300'
                              }`}
                            >
                              {isActive && i < statusMap[itemDetail.statusCode].step - 1 ? (
                                <i className="ri-check-line"></i>
                              ) : (
                                i + 1
                              )}
                            </div>
                            <span
                              className={`text-[10px] mt-1 ${
                                isActive ? 'text-gold-600 font-medium' : 'text-brown-300'
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                          {i < stepLabels.length - 1 && (
                            <div
                              className={`h-0.5 flex-1 mx-1 rounded-full transition-colors ${
                                i < statusMap[itemDetail.statusCode].step - 1 ? 'bg-gold-400' : 'bg-brown-100'
                              }`}
                            ></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Completed banner */}
              {itemDetail.statusCode === 'completed' && (
                <div className="mb-4 p-3 bg-green-50 rounded-xl border border-green-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-green-700 text-sm font-bold">履约已完成</p>
                    <p className="text-green-600/70 text-xs">{itemDetail.completeTime} 完成交割</p>
                  </div>
                </div>
              )}

              {/* Info Grid */}
              <div className="space-y-0 rounded-xl border border-brown-100 overflow-hidden">
                {itemDetail.statusCode === 'processing' ? (
                  <>
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-money-cny-circle-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">预约总额</span>
                      </div>
                      <span className="text-gold-600 text-sm font-bold">¥{itemDetail.lockPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-coins-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">已付定金</span>
                      </div>
                      <span className="text-brown-900 text-sm font-medium">¥{itemDetail.actualPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-scales-3-line text-red-400 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">待付尾款</span>
                      </div>
                      <span className="text-red-500 text-sm font-bold">¥{(itemDetail.lockPrice - itemDetail.actualPrice).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-scales-3-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">预约重量</span>
                      </div>
                      <span className="text-brown-900 text-sm">{itemDetail.weight}g</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-white">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-calendar-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">创建时间</span>
                      </div>
                      <span className="text-brown-900 text-sm">{itemDetail.createTime}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-money-cny-circle-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">锁价金额</span>
                      </div>
                      <span className="text-gold-600 text-sm font-bold">¥{itemDetail.lockPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-coins-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">实际金额</span>
                      </div>
                      <span className="text-brown-900 text-sm font-medium">¥{itemDetail.actualPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-building-2-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">交割金库</span>
                      </div>
                      <span className="text-brown-900 text-sm">{itemDetail.warehouse}</span>
                    </div>
                    {itemDetail.trackingNo && (
                      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-truck-line text-gold-500 text-xs"></i>
                          </div>
                          <span className="text-brown-500 text-sm">物流单号</span>
                        </div>
                        <span className="text-brown-900 text-sm font-mono">{itemDetail.trackingNo}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-brown-100/60">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-calendar-line text-gold-500 text-xs"></i>
                        </div>
                        <span className="text-brown-500 text-sm">创建时间</span>
                      </div>
                      <span className="text-brown-900 text-sm">{itemDetail.createTime}</span>
                    </div>
                    {itemDetail.completeTime && (
                      <div className="flex items-center justify-between px-4 py-3 bg-white">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-time-line text-gold-500 text-xs"></i>
                          </div>
                          <span className="text-brown-500 text-sm">完成时间</span>
                        </div>
                        <span className="text-brown-900 text-sm">{itemDetail.completeTime}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="px-4 pb-6 pt-2 space-y-2">
              {itemDetail.statusCode === 'processing' && (
                <button
                  onClick={() => navigate('/payment')}
                  className="w-full py-3 rounded-xl bg-gold-500 text-brown-950 text-sm font-bold cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors flex items-center justify-center gap-2"
                >
                  <i className="ri-wallet-3-line"></i>
                  去支付尾款
                </button>
              )}
              {itemDetail.statusCode === 'delivering' && itemDetail.trackingNo && (
                <button
                  onClick={() => {}}
                  className="w-full py-3 rounded-xl bg-gold-500 text-brown-950 text-sm font-bold cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors flex items-center justify-center gap-2"
                >
                  <i className="ri-truck-line"></i>
                  查看物流详情
                </button>
              )}
              <button
                onClick={() => setSelectedItem(null)}
                className="w-full py-3 rounded-xl bg-brown-100 text-brown-700 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-brown-200 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}